import React, {FC, useMemo} from 'react';
import moment from "moment";
import { BookedSpaceCalendarView } from "../../../types/model/orm/BookedSpaceCalendarView";
import { adminSpaceUsageReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardBodyActionsContainer,
  ActionsMenuOpenButton,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuItemLabel,
  useMenu
} from "@securspace/securspace-ui-kit";
import {
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem,
  CollapsableCardBodyLinkItem
} from "../../cardComponents";

type AdminSpaceUsageCardListItemProps = {
  item: BookedSpaceCalendarView;
}

/*****************************
 * TODO (CMM): this card is just a rough draft to get all the data in and visible. It needs to be redesigned to
 * better fit the data and be more visually appealing, as well as to focus the most important info into the header
 * and the rest into the footer.
 *
 * Also, copilot had some specific concerns about getOverageSpacesUnChargedCount. Quote:
 *    getOverageSpacesUnChargedCount is calculating totalSpacesBooked - overageSpacesChargedCount, which doesn’t match
 *    the existing space-usage logic (see components/spaceUsage/formatSpaceUsage.js, where uncharged overages are
 *    (maxInventoryCount - totalSpacesBooked) - overageSpacesChargedCount, bounded at 0). Consider using
 *    item.unchargedOverages directly (it exists on BookedSpaceCalendarView) or align the calculation with the
 *    established formula.
 *
 * One possible solution is to use formatSpaceUsageItem from components/spaceUsage/formatSpaceUsage and pass the
 * formatted item to this component, so that all the logic is in one place and consistent across the app.
 *
 ****************************/

const getOverageSpacesUnChargedCount = (item: BookedSpaceCalendarView): number => {
  const unChargedCount = item.totalSpacesBooked - item.overageSpacesChargedCount;
  return unChargedCount > 0 ? unChargedCount : 0;
}

const getInvoiceReportHref = (invoiceNumber: string): string => {
  return `/admin-invoices?invoiceNumber=${invoiceNumber.trim()}`;
}

/**
 * Component that displays all information for a single admin space usage item
 * @param item the BookedSpaceCalendarView to display
 * @constructor
 */
const AdminSpaceUsageCardListItem: FC<AdminSpaceUsageCardListItemProps> = ({ item }) => {

  //hook to handle actions menu state
  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();

  const inventoryLogUrl = useMemo(() => {
    if (item?.overageInvoiceNumbers) {
      const overageInvoices = item.overageInvoiceNumbers.split(",");
      if (overageInvoices.length > 0) {
        return reportEndpoints.actions.downloadInventoryLogById(overageInvoices[0].trim());
      }
    }

    //backup option if there are no overage invoices, should still be able to see the inventory log
    return reportEndpoints.actions.downloadInventoryLogByDate(item.locationId, item.buyerAccountId, moment(item.calendarDate).format('YYYY-MM-DD'))
  }, [item]);

  const handleViewInventoryLog = () => {
    window.open(inventoryLogUrl);
  }

  return (
    <CollapsableCardContainer expanded={true}>
        <CollapsableCardClickableContainer>
          <CollapsableCardHeaderContainer>
            <CollapsableCardHeaderItem value={item?.calendarDate} label={'Date'} />
            <CollapsableCardHeaderItem value={item?.totalSpacesBooked} label={'Booked Spaces'} />
            <CollapsableCardHeaderItem value={item?.maxInventoryCount} label={'Max Inventory'} />
            <CollapsableCardHeaderItem value={item?.overageSpaces} label={'Overage Spaces'} />
            <CollapsableCardBodyItem value={item?.overageSpacesChargedCount} label={'Charged Overages'} />
            <CollapsableCardBodyItem value={getOverageSpacesUnChargedCount(item)} label={'Un-Charged Overages'} />
          </CollapsableCardHeaderContainer>
        </CollapsableCardClickableContainer>
        <CollapsableCardCollapseContainer in={true}>
          <CollapsableCardBodyContainer>
            <CollapsableCardHeaderItem value={item?.durationType} label={'Duration Type'} />
            <CollapsableCardBodyItem value={item?.buyerCompanyName} label={'Buyer'} />
            <CollapsableCardBodyItem value={item?.locationName} label={'Location'} />
            <CollapsableCardBodyItem value={item?.supplierCompanyName} label={'Supplier'} />
            <CollapsableCardBodyItem value={item?.totalSpacesBooked > 0 ? item?.bookingNumbers : ''} label={'Bookings'} />
            <CollapsableCardBodyLinkItem value={item?.overageInvoiceNumbers} label={'Overage Invoice Numbers'} hrefTransformFunction={getInvoiceReportHref}/>
            <CollapsableCardBodyActionsContainer>
              <ActionsMenuOpenButton onClick={handleClick}/>
              {/* @ts-expect-error open is a valid prop. Need to edit uikit to indicate this. */}
              <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
                {/* @ts-expect-error disabled is a valid prop. Need to edit uikit to indicate this. */}
                <ActionsMenuItem onClick={handleViewInventoryLog} disabled={false}>
                  <ActionsMenuItemLabel>View Inventory Log</ActionsMenuItemLabel>
                </ActionsMenuItem>
              </ActionsMenu>
            </CollapsableCardBodyActionsContainer>
          </CollapsableCardBodyContainer>
        </CollapsableCardCollapseContainer>
      </CollapsableCardContainer>
  );
};

export { AdminSpaceUsageCardListItem };